import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { adminResetUserPassword, adminGetProfileByEmail, adminGetProfileByPhone } from '../../services/admin';
import useAuth from '../hooks/useAuth';
import { countryCodes } from '../constants/Constants';

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneNumber = (phoneNumber) => {
  const phoneRegex = /^(\+?\d{1,3}[-.\s]?)?\d{10}$/;
  return phoneRegex.test(phoneNumber);
};

const AdminPasswordManagement = () => {
  const { getToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchMode, setSearchMode] = useState('phone');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [name, setName] = useState('Not Available');
  const [countryCode, setCountryCode] = useState('+91');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const fetchUser = async () => {
    const token = getToken()?.token;
    if (!token || !searchText) {
      toast.error('Please provide valid input.');
      return;
    }
  
    setIsLoading(true);
    try {
      let response;
      if (searchMode === 'phone') {
        if (!validatePhoneNumber(countryCode + searchText)) {
          toast.error('Please provide a valid phone number.');
          setIsLoading(false);
          return;
        }
        // Use the service function to get profile by phone
        response = await adminGetProfileByPhone(token, countryCode + searchText);
      } else if (searchMode === 'email') {
        if (!validateEmail(searchText)) {
          toast.error('Please provide a valid email address.');
          setIsLoading(false);
          return;
        }
        // Use the service function to get profile by email
        response = await adminGetProfileByEmail(token, searchText);
      }
  
      // Use response.data directly since we are using axios
      const userData = searchMode === 'phone' ? response.data?.data?.adminGetProfileByPhone : response.data?.data?.adminGetProfileByEmail;
  
      if (!userData || (!userData.user.phone && !userData.user.email)) {
        toast.info('No user found with the provided information.');
        setUsers([]);
        setIsLoading(false);
        return;
      }
  
      const userWithId = {
        id: userData.profile.id,
        phone: userData.user.phone || '',
        email: userData.user.email || '',
        disabled: userData.user.disabled,
      };
  
      setUsers([userWithId]);
      setName(`${userData.profile.firstName} ${userData.profile.lastName}`);
      setSelectedUser(userWithId);
    } catch (error) {
      toast.error('Error fetching user data.');
      console.error(error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResetPassword = async () => {
    const token = getToken()?.token;
    if (!token || !selectedUser || !newPassword) {
      toast.error('Please provide all details.');
      return;
    }
    setIsLoading(true);
    try {
      await adminResetUserPassword(token, selectedUser.id, newPassword);
      toast.success('Password updated successfully.');
      setNewPassword('');
    } catch (error) {
      toast.error('Error resetting password.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { field: 'phone', headerName: 'Phone/Email', flex: 1, valueGetter: (params) => params.row.phone || params.row.email },
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1, 
      renderCell: (params) => (
        <Typography
          component="a"
          href={`/administratorpath/viewprofile/${params.row.id}`}
          sx={{ 
            textDecoration: 'none',
            color: 'primary.main',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {name}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          startIcon={<LockResetIcon />}
          onClick={() => setSelectedUser(params.row)}
        >
          Reset Password
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ padding: '20px', maxWidth: '90%', margin: 'auto' }}>
      <ToastContainer position="top-right" autoClose={5000} />
      <Typography variant="h5" gutterBottom sx={{ mb: 4 }} fontWeight={600}>
        Admin Password Management
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchMode}
                onChange={(e) => setSearchMode(e.target.value)}
                label="Search By"
              >
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {searchMode === 'phone' && (
            <Grid item xs={12}>
              <Autocomplete
                disableClearable
                options={countryCodes}
                getOptionLabel={(option) => `${option.code} (${option.country})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country Code"
                    variant="outlined"
                  />
                )}
                value={countryCodes.find(code => code.code === countryCode)}
                onChange={(event, newValue) => {
                  setCountryCode(newValue.code);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={searchMode === 'phone' ? 'Phone Number' : 'Email Address'}
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  fetchUser();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={fetchUser} disabled={isLoading}>
                      {isLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {users.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            User Information
          </Typography>
          <DataGrid
            rows={users}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            autoHeight
            disableSelectionOnClick
            getRowId={(row) => row.id}
            sx={{ mb: 2 }}
          />
        </Paper>
      )}

      {selectedUser && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Reset Password
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleResetPassword}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={24} /> : <LockResetIcon />}
                className="text-center font-semibold text-md bg-blue-700 rounded-md w-full text-white px-2 py-3"
              >
                Reset Password
              </button>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default AdminPasswordManagement;